<template>
  <div id="page">
    <!-- loading progress bar -->
    <div v-if="loadingPercent < 100" class="loading--progress--bar">
      <div class="loading--bar--inner" :style="{ width: loaded }" />
    </div>
    <!-- component -->
    <slot />
  </div>
</template>

<script>
import appConfig from "@/siteConfig.json";
import {
  extractInstagramUsername,
  extractTwitterUsername,
  trimText,
} from "@/helpers";
import { useGlobalStore } from "@/store/Modules/global";
import { mapState } from "pinia";
import { computed } from "vue";
import { useRoute, useCookie } from "#app";
import logo from "@/assets/logo.jpg";
export default {
  setup(props) {
    const {
      title,
      description,
      keywords,
      image,
      imageAlt,
      instagram,
      twitter,
      pageType,
      sku,
      productOfferSchema,
      brand,
      isServerSide,
      disableIndexing,
      aggregateRating,
      reviews,
    } = props;

    // initial metas injection on the server side
    const route = useRoute();
    const usedCookieLanguage =
      useCookie("localStorageCopy")?.value?.language || "ar";

    console.log("PagePortal setup - ALL PROPS:", props);
    console.log("PagePortal setup - title:", title);
    console.log("PagePortal setup - description:", description);
    console.log("PagePortal setup - keywords:", keywords);
    console.log("PagePortal setup - imageAlt:", imageAlt);

    // Create local variables instead of modifying parameters directly
    let instagramHandle = instagram ? extractInstagramUsername(instagram) : "";
    let twitterHandle = twitter ? extractTwitterUsername(twitter) : "";

    const maximumTitleLength = 55;
    const maximumDescriptionLength = 120;
    const maximumKeywordsLength = 255;

    const newTitle = trimText(
      title || appConfig.title[usedCookieLanguage],
      maximumTitleLength
    );
    const newDescription = trimText(
      description || appConfig.description,
      maximumDescriptionLength
    );
    console.log("newKeywords from PagePortal:", keywords);
    const newKeywords = Array.isArray(keywords)
      ? trimText(
          keywords.join(", ") || appConfig.keywords,
          maximumKeywordsLength
        )
      : trimText(keywords || appConfig.keywords, maximumKeywordsLength);
    function getImageMimeType(url) {
      if (!url) return "image/jpeg";
      if (url.endsWith(".png")) return "image/png";
      if (url.endsWith(".gif")) return "image/gif";
      if (url.endsWith(".webp")) return "image/webp";
      return "image/jpeg"; // Default for .jpg, .jpeg or unknown
    }

    // Use default image if none provided
    const defaultImage = appConfig.defaultImage || logo;
    const imageUrl = image || defaultImage;

    // Ensure image URL is absolute and properly formatted
    let absoluteImageUrl;
    if (imageUrl.startsWith("http")) {
      absoluteImageUrl = imageUrl;
    } else {
      // Handle both relative paths starting with / and without /
      const cleanPath = imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;
      absoluteImageUrl = `${appConfig.url}${cleanPath}`;
    }

    function truncateUnicodeString(str, length) {
      if (str.length <= length) return str;
      str = str.replace(/[!@#$%^&*()_+|~=`{}\[\]:";'<>?,.\/\\-]/g, "");
      let truncatedArray = Array.from(str).slice(0, length);
      console.log("truncatedArray from truncateUnicodeString:", truncatedArray);
      return truncatedArray.join("");
    }

    // Ensure Product schema has all required fields
    const ensureProductSchema = (schema) => {
      if (pageType === "Product") {
        // Product schema requires: name, description, image, offers
        return {
          ...schema,
          name: schema.name || truncateUnicodeString(newTitle, 30),
          description:
            schema.description || truncateUnicodeString(newDescription, 50),
          image: schema.image || {
            "@type": "ImageObject",
            url: absoluteImageUrl,
            contentUrl: absoluteImageUrl,
            width: "1200",
            height: "630",
          },
        };
      }
      return schema;
    };

    const structuredData = {
      "@context": "https://schema.org/",
      "@type": pageType || "WebPage",
      headline: truncateUnicodeString(newTitle, 30),
      name: truncateUnicodeString(newTitle, 30),
      // Always include description for Product schema
      ...(pageType === "Product" && {
        description: truncateUnicodeString(newDescription, 50),
      }),
      ...(image && {
        image: {
          "@type": "ImageObject",
          url: absoluteImageUrl,
          contentUrl: absoluteImageUrl,
          width: "1200",
          height: "630",
        },
      }),
      // Include description for non-Product schemas
      ...(pageType !== "Product" &&
        description && {
          description: truncateUnicodeString(newDescription, 50),
        }),
      ...(sku && /^\d+$/.test(sku) && { sku: sku }),
      ...(brand && {
        brand: {
          "@type": "Brand",
          name: brand,
        },
      }),
      ...(productOfferSchema &&
        productOfferSchema !== null &&
        Object.keys(productOfferSchema).length > 0 && {
          offers: productOfferSchema,
        }),
      // Only include aggregateRating and review if they have valid data
      ...(aggregateRating &&
        Object.keys(aggregateRating).length > 0 && {
          aggregateRating: aggregateRating,
        }),
      ...(reviews &&
        reviews.length > 0 && {
          review: reviews,
        }),
    };

    // Apply Product schema validation
    const finalStructuredData = ensureProductSchema(structuredData);

    const getIndexing = () => {
      const protectedPaths = [
        "/admin",
        "/moderator",
        "/reseller",
        "/sub_reseller",
        "/panel",
      ];
      protectedPaths.some((path) => route.path.startsWith(path));
      const isProtectedPath = protectedPaths.some((path) =>
        route.path.startsWith(path)
      );
      if (isProtectedPath) {
        return "noindex, nofollow";
      } else if (disableIndexing) {
        return "noindex, nofollow";
      } else {
        return "index, follow";
      }
    };

    const indexing = getIndexing();
    useHead({
      title: `${title || appConfig.title[usedCookieLanguage]}`,
      link: [
        {
          rel: "canonical",
          href: () => `${appConfig.url}${route?.fullPath}`,
          tagPriority: "critical",
        },
      ],
      meta: [
        { itemProp: "name", content: newTitle, tagPriority: "critical" },
        { name: "title", content: newTitle, tagPriority: "critical" },
        {
          name: "description",
          content: newDescription,
          tagPriority: "critical",
        },
        { name: "keywords", content: newKeywords, tagPriority: "critical" },
        { property: "og:title", content: newTitle, tagPriority: "critical" },
        {
          property: "og:description",
          content: newDescription,
          tagPriority: "critical",
        },
        {
          property: "og:site_name",
          content: appConfig[`title_${usedCookieLanguage}`],
          tagPriority: "critical",
        },
        {
          property: "og:url",
          content: `${appConfig.url}${route.fullPath}`,
          tagPriority: "critical",
        },
        {
          property: "og:locale",
          content: usedCookieLanguage,
          tagPriority: "critical",
        },
        {
          property: "og:type",
          content: pageType == "Product" ? "product" : "website",
          tagPriority: "critical",
        },
        {
          property: "og:updated_time",
          content: Date.now().toString(),
          tagPriority: "critical",
        },
        { name: "twitter:title", content: newTitle, tagPriority: "critical" },
        {
          name: "twitter:description",
          content: newDescription,
          tagPriority: "critical",
        },
        {
          name: "apple-mobile-web-app-title",
          content: appConfig[`title_${usedCookieLanguage}`],
          tagPriority: "critical",
        },

        // Always include image tags, using default if needed
        { name: "image", content: absoluteImageUrl, tagPriority: "critical" },
        {
          property: "og:image",
          content: absoluteImageUrl,
          tagPriority: "critical",
        },
        {
          property: "og:image:url",
          content: absoluteImageUrl,
          tagPriority: "critical",
        },
        {
          property: "og:image:secure_url",
          content: absoluteImageUrl,
          tagPriority: "critical",
        },
        {
          property: "og:image:type",
          content: getImageMimeType(absoluteImageUrl),
          tagPriority: "critical",
        },
        {
          property: "og:image:width",
          content: "1200",
          tagPriority: "critical",
        },
        {
          property: "og:image:height",
          content: "630",
          tagPriority: "critical",
        },
        {
          property: "og:image:alt",
          content: imageAlt || newDescription,
          tagPriority: "critical",
        },
        {
          name: "twitter:card",
          content: "summary_large_image",
          tagPriority: "critical",
        },
        {
          name: "twitter:image",
          content: absoluteImageUrl,
          tagPriority: "critical",
        },
        {
          name: "twitter:image:alt",
          content: imageAlt || newDescription,
          tagPriority: "critical",
        },

        // Additional meta tags for better social media compatibility
        {
          property: "og:image:url",
          content: absoluteImageUrl,
          tagPriority: "critical",
        },
        {
          property: "og:image:secure_url",
          content: absoluteImageUrl,
          tagPriority: "critical",
        },
        {
          name: "twitter:image:src",
          content: absoluteImageUrl,
          tagPriority: "critical",
        },

        // Additional meta tags for better social media sharing
        {
          name: "author",
          content: appConfig[`title_${usedCookieLanguage}`],
          tagPriority: "critical",
        },
        {
          name: "application-name",
          content: appConfig[`title_${usedCookieLanguage}`],
          tagPriority: "critical",
        },
        { property: "og:determiner", content: "auto", tagPriority: "critical" },

        ...(instagramHandle
          ? [
              {
                name: "instagram:site",
                content: `@${instagramHandle}`,
                tagPriority: "critical",
              },
            ]
          : []),
        ...(twitterHandle
          ? [
              {
                name: "twitter:site",
                content: `@${twitterHandle}`,
                tagPriority: "critical",
              },
            ]
          : []),
        ...(appConfig.facebook_app_id
          ? [
              {
                property: "fb:app_id",
                content: appConfig.facebook_app_id,
                tagPriority: "critical",
              },
            ]
          : []),
        { name: "robots", content: indexing, tagPriority: "critical" },
      ],
      ...(isServerSide && {
        script: [
          {
            type: "application/ld+json",
            children: JSON.stringify(finalStructuredData),
            tagPriority: "critical",
          },
        ],
      }),
    });

    // Debug structured data in development
    if (process.dev && isServerSide) {
      // Check if at least one of the required fields is present
      const hasRequiredFields =
        finalStructuredData.offers ||
        finalStructuredData.aggregateRating ||
        finalStructuredData.review;
      console.log(
        "Has required fields (offers/aggregateRating/review):",
        !!hasRequiredFields
      );
    }

    // Debug aggregate rating and reviews in development
    if (process.dev) {
      console.log("Aggregate Rating:", aggregateRating);
      console.log("Reviews:", reviews);
    }
  },
  data() {
    return {
      loadingPercent: 0,
      loadTime: 0,
      interval: null,
      usedCookieLanguage: "ar", // Default value, will be updated in mounted
    };
  },
  props: {
    title: {
      type: String,
      required: false,
      default: appConfig.title_ar,
    },
    description: {
      type: String,
      required: false,
      default: appConfig.description,
    },
    keywords: {
      type: [String, Array],
      required: false,
      default: "",
    },
    image: {
      type: String,
      required: false,
      default: "",
    },
    imageAlt: {
      type: String,
      required: false,
      default: "",
    },
    twitter: {
      type: String,
      required: false,
      default: "",
    },
    instagram: {
      type: String,
      required: false,
      default: "",
    },
    isServerSide: {
      type: Boolean,
      required: false,
      default: false,
    },
    pageType: {
      type: String,
      required: false,
      default: "",
    },
    sku: {
      type: [String, Number],
      required: false,
      default: "",
    },
    productOfferSchema: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    brand: {
      type: String,
      required: false,
      default: "",
    },
    disableIndexing: {
      type: Boolean,
      required: false,
      default: false,
    },
    aggregateRating: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    reviews: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  methods: {
    doProgress() {
      let step = this.loadTime / 100;
      this.interval = setInterval(() => {
        this.loadingPercent++;
      }, step);
    },
    updateHeadMetas() {
      //default take from props
      let siteData = {
        title: this.title,
        description: this.description,
        keywords: this.keywords,
        image: this.image || logo,
      };
      console.log("siteData from updateHeadMetas:", siteData);
      //at route change
      const route = this.$route;
      const singleProductRouteName = "products-storeusername-id-productname";
      const singleStoreRouteName = "store-username";

      try {
        if (
          !(
            route.name == singleStoreRouteName ||
            route.name == singleProductRouteName
          )
        ) {
          siteData = {
            ...siteData,
            title: this.layout?.meta
              ? this.layout?.meta[this.anyKey("meta_title")]
              : "cezma",
            description: this.layout?.meta
              ? this.layout?.meta[this.anyKey("meta_description")]
              : "cezma description",
            // keywords: this.layout?.meta?.meta_keywords,
          };
        }
      } catch (error) {
        console.error("Error updating meta data:", error);
        // Fallback values if store data is not available
        siteData = {
          ...siteData,
          title: "cezma",
          description: "cezma description",
          keywords: "",
        };
      }

      function getImageMimeType(url) {
        if (!url) return "image/jpeg";
        if (url.endsWith(".png")) return "image/png";
        if (url.endsWith(".gif")) return "image/gif";
        if (url.endsWith(".webp")) return "image/webp";
        return "image/jpeg"; // Default for .jpg, .jpeg or unknown
      }

      const updatedTitleWithNotificationCount =
        this.notificationsCount > 0
          ? `${siteData.title} (${this.notificationsCount})`
          : siteData.title;

      // Ensure image URL is absolute for client-side updates
      let absoluteImageUrl;
      // if (siteData.image.startsWith('http')) {
      //   absoluteImageUrl = siteData.image;
      // } else {
      //   // Handle both relative paths starting with / and without /
      //   const cleanPath = siteData.image.startsWith('/') ? siteData.image : `/${siteData.image}`;
      //   absoluteImageUrl = `${appConfig.url}${cleanPath}`;
      // }
      absoluteImageUrl = siteData.image;
      // Update meta data on the client side using document manipulation
      if (process.client) {
        try {
          // Update title
          // document.title = updatedTitleWithNotificationCount;

          // Update meta tags
          this.updateMetaTag("name", "title", siteData.title);
          // this.updateMetaTag('name', 'description', siteData?.description || 'description');
          this.updateMetaTag("property", "og:title", siteData.title);
          this.updateMetaTag(
            "property",
            "og:description",
            siteData.description
          );
          this.updateMetaTag(
            "property",
            "og:site_name",
            appConfig[`title_${this.usedCookieLanguage}`]
          );
          this.updateMetaTag(
            "property",
            "og:url",
            `${appConfig.url}${this.$route.fullPath}`
          );
          this.updateMetaTag("property", "og:locale", this.usedCookieLanguage);
          this.updateMetaTag("name", "twitter:title", siteData.title);
          this.updateMetaTag(
            "name",
            "twitter:description",
            siteData.description
          );
          this.updateMetaTag(
            "name",
            "apple-mobile-web-app-title",
            appConfig[`title_${this.usedCookieLanguage}`]
          );

          // Update image tags
          this.updateMetaTag("name", "image", absoluteImageUrl);
          this.updateMetaTag("itemprop", "image", absoluteImageUrl);
          this.updateMetaTag("property", "og:image", absoluteImageUrl);
          this.updateMetaTag("property", "og:image:url", absoluteImageUrl);
          this.updateMetaTag(
            "property",
            "og:image:secure_url",
            absoluteImageUrl
          );
          this.updateMetaTag(
            "property",
            "og:image:type",
            getImageMimeType(absoluteImageUrl)
          );
          this.updateMetaTag("property", "og:image:width", "1200");
          this.updateMetaTag("property", "og:image:height", "630");
          // this.updateMetaTag("property", "og:image:alt", siteData.description);
          this.updateMetaTag("name", "twitter:image", absoluteImageUrl);
          this.updateMetaTag("name", "twitter:image:src", absoluteImageUrl);
          // this.updateMetaTag("name", "twitter:image:alt", siteData.description);

          // Update robots meta
          this.updateMetaTag(
            "name",
            "robots",
            this.disableIndexing ? "noindex, nofollow" : "index, follow"
          );

          // Update structured data
          this.updateStructuredData();
        } catch (error) {
          console.error("Error updating meta tags:", error);
        }
      }
    },
    updateMetaTag(attr, value, content, priority = "critical") {
      if (process.client) {
        try {
          let meta = document.querySelector(`meta[${attr}="${value}"]`);
          if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute(attr, value);
            document.head.appendChild(meta);
          }
          meta.setAttribute("content", content);
          meta.setAttribute("tagPriority", priority);
        } catch (error) {
          console.error("Error updating meta tag:", error);
        }
      }
    },
    updateStructuredData() {
      if (process.client) {
        try {
          // Remove existing structured data
          const existingScripts = document.querySelectorAll(
            'script[type="application/ld+json"]'
          );
          existingScripts.forEach((script) => {
            if (
              script.textContent.includes('"@type":"Product"') ||
              script.textContent.includes('"@type":"WebPage"')
            ) {
              script.remove();
            }
          });

          // Add new structured data
          const script = document.createElement("script");
          script.type = "application/ld+json";
          script.textContent = JSON.stringify(this.generateStructuredData());
          document.head.appendChild(script);
        } catch (error) {
          console.error("Error updating structured data:", error);
        }
      }
    },
    generateStructuredData() {
      try {
        const truncateUnicodeString = (str, length) => {
          if (!str || str.length <= length) return str;
          str = str.replace(/[!@#$%^&*()_+|~=`{}\[\]:";'<>?,.\/\\-]/g, "");
          let truncatedArray = Array.from(str).slice(0, length);
          return truncatedArray.join("");
        };

        const siteData = {
          title: this.title,
          description: this.description,
          keywords: this.keywords,
          image: this.image || logo,
        };

        // Ensure image URL is absolute
        let absoluteImageUrl;
        // if (siteData.image.startsWith('http')) {
        //   absoluteImageUrl = siteData.image;
        // } else {
        //   // Handle both relative paths starting with / and without /
        //   const cleanPath = siteData.image.startsWith('/') ? siteData.image : `/${siteData.image}`;
        //   absoluteImageUrl = `${appConfig.url}${cleanPath}`;
        // }
        absoluteImageUrl = siteData.image;

        const route = this.$route;
        const singleProductRouteName = "products-storeusername-id-productname";
        const singleStoreRouteName = "store-username";

        if (
          !(
            route.name == singleStoreRouteName ||
            route.name == singleProductRouteName
          )
        ) {
          if (this.layout?.meta) {
            siteData.title =
              this.layout.meta[this.anyKey("meta_title")] || "cezma";
            siteData.description =
              this.layout.meta[this.anyKey("meta_description")] ||
              "cezma description";
            siteData.keywords =
              this.layout.meta.meta_keywords || siteData.keywords;
          }
        }

        // Ensure Product schema has all required fields
        const ensureProductSchema = (schema) => {
          if (this.pageType === "Product") {
            // Product schema requires: name, description, image, offers
            return {
              ...schema,
              name: schema.name || truncateUnicodeString(siteData.title, 30),
              description:
                schema.description ||
                truncateUnicodeString(siteData.description, 50),
              image: schema.image || {
                "@type": "ImageObject",
                url: absoluteImageUrl,
                contentUrl: absoluteImageUrl,
                width: "1200",
                height: "630",
              },
            };
          }
          return schema;
        };

        const structuredData = {
          "@context": "https://schema.org/",
          "@type": this.pageType || "WebPage",
          headline: truncateUnicodeString(siteData.title, 30),
          name: truncateUnicodeString(siteData.title, 30),
          // Always include description for Product schema
          ...(this.pageType === "Product" && {
            description: truncateUnicodeString(siteData.description, 50),
          }),
          ...(siteData.image && {
            image: {
              "@type": "ImageObject",
              url: absoluteImageUrl,
              contentUrl: absoluteImageUrl,
              width: "1200",
              height: "630",
            },
          }),
          // Include description for non-Product schemas
          ...(this.pageType !== "Product" &&
            siteData.description && {
              description: truncateUnicodeString(siteData.description, 50),
            }),
          ...(this.sku && /^\d+$/.test(this.sku) && { sku: this.sku }),
          ...(this.brand && {
            brand: {
              "@type": "Brand",
              name: this.brand,
            },
          }),
          ...(this.productOfferSchema &&
            this.productOfferSchema !== null &&
            Object.keys(this.productOfferSchema).length > 0 && {
              offers: this.productOfferSchema,
            }),
          ...(this.aggregateRating &&
            Object.keys(this.aggregateRating).length > 0 && {
              aggregateRating: this.aggregateRating,
            }),
          ...(this.reviews &&
            this.reviews.length > 0 && {
              review: this.reviews,
            }),
        };

        // Apply Product schema validation
        const finalStructuredData = ensureProductSchema(structuredData);
        return finalStructuredData;
      } catch (error) {
        console.error("Error generating structured data:", error);
        // Return basic fallback structured data
        return {
          "@context": "https://schema.org/",
          "@type": "WebPage",
          name: "cezma",
          description: "cezma description",
        };
      }
    },
    anyKey(key) {
      try {
        // Get the current language from the component's usedCookieLanguage
        const isArabic = this.usedCookieLanguage === "ar";
        return `${key}_${isArabic ? "ar" : "en"}`;
      } catch (error) {
        console.error("Error in anyKey:", error);
        return `${key}_ar`; // Fallback to Arabic
      }
    },
  },
  computed: {
    ...mapState(useGlobalStore, ["layout", "notificationsCount"]),
    loaded() {
      return this.loadingPercent + "%";
    },
  },
  watch: {
    description() {
      this.updateHeadMetas();
    },
    title() {
      this.updateHeadMetas();
    },
    $route: {
      handler(newValue, oldValue) {
        this.updateHeadMetas();
      },
    },
    image() {
      this.updateHeadMetas();
    },
    notificationsCount() {
      this.updateHeadMetas();
    },
    layout() {
      this.updateHeadMetas();
    },
    aggregateRating() {
      this.updateHeadMetas();
    },
    reviews() {
      this.updateHeadMetas();
    },
    isRTL() {
      this.updateHeadMetas();
    },
    loadingPercent(val) {
      if (val >= 100) {
        this.loadTime = 0;
        clearInterval(this.interval);
      }
    },
  },
  mounted() {
    if (process.client) {
      try {
        let perfData = window.performance.timing;
        let estimatedTime = Math.abs(
          perfData.loadEventEnd - perfData.navigationStart
        );
        this.loadTime = parseInt((estimatedTime / 1000) % 60) * 100;
        this.doProgress();

        // Initialize cookie language on client side
        const cookie = useCookie("localStorageCopy");
        this.usedCookieLanguage = cookie?.value?.language || "ar";
      } catch (error) {
        console.warn("Error in mounted hook:", error);
        this.loadTime = 3000; // Fallback load time
        this.doProgress();
        this.usedCookieLanguage = "ar";
      }
    }
  },
};
</script>

<style lang="scss" scoped>
#page {
  /**/
  /*unexpected behaviour due to  header now is fixed postion so clear header heigth from this calc*/
  /*min-height: 300px*/
  min-height: calc(100vh - var(--main-footer-height));

  .loading--progress--bar {
    width: 100%;
    position: fixed;
    z-index: var(--zLoadingProgressBar);
    height: 3px;
    background-color: var(--borders-gray);
    top: 0;
    left: 0;
    opacity: 9;
    .loading--bar--inner {
      transition: all 350ms linear 0s;
      width: 0%;
      border-radius: 0 5px 0 5px;
      background-color: var(--secondary-clr);
      height: 100%;
      user-select: none;
    }
  }
}
</style>
